<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onScopeDispose,
    ref,
    shallowRef,
    watch,
  } from 'vue';
  import { useClipboard, useEventListener, useNow } from '@vueuse/core';
  import { ReservationsService } from '@/api/services/reservations.service';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import { useReservationActions } from '@/composition/reservations/use-reservation-actions';
  import { useReservationNotesAutosave } from '@/composition/reservations/use-reservation-notes-autosave';
  import { registerReservationBranchLeave } from '@/composition/reservations/reservation-branch-leave';
  import { useAuthStore } from '@/composition/use-auth';
  import { useUi } from '@/composition/use-ui';
  import { lateMinutes } from '@/domain/reservations/reservation-status';
  import type { IStaffActionId } from '@/domain/reservations/reservation-status';
  import {
    formatDateLong,
    formatTime,
    getReservationTimeZone,
    reservationCutoverHour,
  } from '@/domain/reservations/reservation-time';
  import { PermissionType } from '@/enums/PermissionType';
  import { AlertType } from '@/interfaces/IAlert';
  import type {
    IReservation,
    IReservationEvent,
  } from '@/interfaces/IReservation';
  import { useReservationsStore } from '@/stores/reservations.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import V2ReservationGuestCard from './V2ReservationGuestCard.vue';
  import V2ReservationDepositCard from './V2ReservationDepositCard.vue';
  import V2ReservationNotesEditor from './V2ReservationNotesEditor.vue';
  import V2ReservationDetailHeader from './V2ReservationDetailHeader.vue';
  import V2ReservationDetailFooter from './V2ReservationDetailFooter.vue';
  import V2ReservationEventsList from './V2ReservationEventsList.vue';
  import V2ReservationStatusActions from './V2ReservationStatusActions.vue';

  const props = defineProps<{
    reservationId: number | null;
    mode: 'column' | 'sheet';
  }>();
  const emit = defineEmits<{
    (event: 'close'): void;
    (event: 'edit', reservation: IReservation): void;
    (event: 'guest', customerId: number): void;
  }>();
  const store = useReservationsStore();
  const restaurant = useRestaurantStore();
  const auth = useAuthStore();
  const ui = useUi();
  const clipboard = useClipboard({ legacy: true });
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const row = shallowRef<IReservation | null>(null);
  const events = shallowRef<IReservationEvent[]>([]);
  const loading = ref(false);
  const failed = ref(false);
  const historyOpen = ref(false);
  const content = ref<HTMLElement>();
  const sheetOpen = computed(
    () => props.mode === 'sheet' && props.reservationId != null
  );
  let overlayHost: HTMLElement | null = null;
  const now = useNow({ interval: 30000 });
  let alive = true;
  let sequence = 0;
  const canManage = computed(() =>
    auth.permissionSet.has(PermissionType.ReservationsManage)
  );
  const canOrders = computed(() =>
    auth.permissionSet.has(PermissionType.OrdersManage)
  );
  const zone = computed(() =>
    getReservationTimeZone(
      restaurant.selectedBranch?.location,
      restaurant.restaurant?.country
    )
  );
  const cutover = computed(() =>
    reservationCutoverHour(restaurant.selectedBranch?.businessDayStartHour)
  );
  const late = computed(() =>
    row.value
      ? lateMinutes(
          row.value,
          now.value,
          store.day?.settingsSummary.toleranceMin ?? 15
        )
      : 0
  );
  const editable = computed(
    () =>
      canManage.value &&
      !!row.value &&
      ['pending_payment', 'pending', 'confirmed', 'arrived'].includes(
        row.value.status
      )
  );
  const actions = useReservationActions(row, {
    onUpdated: applyUpdated,
    onStale: () => void load(),
  });
  const drafts = useReservationNotesAutosave(row, {
    blocked: actions.busy,
    canSave: canManage,
    onSaved: (value) => {
      if (!alive) return;
      store.patchReservation(value);
      applyUpdated(value);
    },
    onError: (error) => {
      if (alive) ui.serverError(error, t('reservations.detail.saveError'));
    },
  });
  const {
    notes: draftNotes,
    tags: draftTags,
    dirty,
    error: draftError,
  } = drafts;
  const busy = computed(() => actions.busy.value || drafts.saving.value);
  function applyUpdated(value: IReservation) {
    if (
      !alive ||
      value.id !== props.reservationId ||
      value.branchId !== restaurant.selectedBranch?.id ||
      value.restaurantId !== restaurant.restaurant?.id
    )
      return;
    sequence++; // A detail read requested before this mutation is now stale.
    acceptRow(value);
  }
  function acceptRow(value: IReservation) {
    row.value = value;
  }

  async function load() {
    const request = ++sequence;
    const id = props.reservationId;
    const branchId = restaurant.selectedBranch?.id;
    if (!id || !branchId || !canManage.value) {
      loading.value = false;
      return;
    }
    loading.value = true;
    failed.value = false;
    try {
      const result = await ReservationsService.getOne(id);
      if (
        !alive ||
        request !== sequence ||
        props.reservationId !== id ||
        branchId !== restaurant.selectedBranch?.id
      )
        return;
      if (result.branchId !== branchId) {
        failed.value = true;
        return;
      }
      acceptRow(result);
      events.value = result.events;
    } catch (error) {
      if (!alive || request !== sequence) return;
      failed.value = true;
      ui.serverError(error, t('reservations.page.loadError'));
    } finally {
      if (alive && request === sequence) loading.value = false;
    }
  }

  function notesChanged(value: string) {
    drafts.setNotes(value);
  }
  function tagsChanged(value: string[]) {
    drafts.setTags(value);
  }
  watch(
    () => [props.reservationId, restaurant.selectedBranch?.id, canManage.value],
    () => {
      sequence++;
      const cached = canManage.value
        ? store.day?.reservations.find(
            (value) =>
              value.id === props.reservationId &&
              value.branchId === restaurant.selectedBranch?.id
          )
        : null;
      row.value = cached ?? null;
      events.value = [];
      failed.value = false;
      historyOpen.value = false;
      void load();
    },
    { immediate: true, flush: 'sync' }
  );
  watch(
    () => store.revision,
    () => {
      if (props.reservationId) void load();
    }
  );
  const viewKey = () =>
    `${restaurant.restaurant?.id}:${restaurant.selectedBranch?.id}:${props.reservationId}`;
  async function flushCurrent() {
    const current = viewKey();
    return (
      (await drafts.afterFlush(() => {})) && alive && viewKey() === current
    );
  }
  const removeBranchGuard = registerReservationBranchLeave(flushCurrent);
  const removeRouteGuard = proxy.$router.beforeEach((to, from, next) => {
    if (to.fullPath === from.fullPath) {
      next();
      return;
    }
    void flushCurrent()
      .then((saved) => (saved ? next() : next(false)))
      .catch(() => next(false));
  });
  useEventListener(window, 'beforeunload', (event: BeforeUnloadEvent) => {
    if (!dirty.value) return;
    event.preventDefault();
    event.returnValue = '';
  });
  watch(
    [() => props.mode, () => props.reservationId, content],
    () => {
      const candidate =
        props.mode === 'sheet' && props.reservationId != null
          ? content.value?.closest('.content-shell')
          : null;
      const host = candidate instanceof HTMLElement ? candidate : null;
      if (host === overlayHost) return;
      overlayHost?.classList.remove('reservation-detail-overlay-host');
      overlayHost = host;
      overlayHost?.classList.add('reservation-detail-overlay-host');
    },
    { immediate: true, flush: 'post' }
  );
  onScopeDispose(() => {
    alive = false;
    sequence++;
    removeRouteGuard();
    removeBranchGuard();
    overlayHost?.classList.remove('reservation-detail-overlay-host');
    overlayHost = null;
  });
  async function close() {
    if (await flushCurrent()) emit('close');
  }
  async function edit() {
    if ((await flushCurrent()) && row.value && editable.value)
      emit('edit', row.value);
  }
  async function guest() {
    if ((await flushCurrent()) && row.value?.customerId)
      emit('guest', row.value.customerId);
  }
  async function runAction(action: IStaffActionId) {
    if (await flushCurrent()) await actions.run(action);
  }
  async function setTables() {
    if (await flushCurrent()) await actions.setTables();
  }
  async function applyDeposit() {
    if (await flushCurrent()) await actions.applyDeposit();
  }
  async function refundPaidDeposit() {
    if (await flushCurrent())
      await actions.refundDeposit(undefined, 'reservations.deposit.refundPaid');
  }
  async function resend() {
    if (await flushCurrent()) await actions.resend();
  }
  async function copyPhone() {
    const value = row.value?.guestPhone;
    if (!value) return;
    try {
      await clipboard.copy(value);
      if (alive)
        ui.showToast(AlertType.Success, t('reservations.detail.phoneCopied'));
    } catch (error) {
      if (alive) ui.serverError(error);
    }
  }
  function formattedDate(value: string) {
    return formatDateLong(value, zone.value, proxy.$i18n.locale);
  }
  function formattedTime(value: string) {
    return formatTime(value, zone.value, proxy.$i18n.locale);
  }
  function keydown(event: KeyboardEvent) {
    if (props.mode !== 'sheet') return;
    if (
      event.key === 'Escape' &&
      !(
        event.target instanceof Element &&
        event.target.closest('.pos-ui-popover')
      )
    ) {
      event.preventDefault();
      close();
    }
    if (event.key !== 'Tab' || !content.value) return;
    const focusable = [
      ...content.value.querySelectorAll<HTMLElement>(
        'button:not(:disabled),a[href],input:not(:disabled),textarea:not(:disabled),select:not(:disabled),[tabindex="0"]'
      ),
    ].filter((element) => element.offsetParent !== null);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  }
  watch(
    () => props.reservationId,
    async (id) => {
      if (!id || props.mode !== 'sheet') return;
      await nextTick();
      if (
        alive &&
        props.reservationId === id &&
        !content.value?.contains(document.activeElement)
      )
        content.value?.focus();
    }
  );
</script>

<template>
  <aside
    v-show="mode === 'column' || reservationId != null"
    class="reservation-detail"
    :class="`is-${mode}`"
    @keydown="keydown"
  >
    <button
      v-show="mode === 'sheet'"
      class="detail-backdrop"
      type="button"
      tabindex="-1"
      :aria-label="$t('close')"
      @click="close"
    />
    <section
      ref="content"
      class="detail-content"
      tabindex="-1"
      :role="
        sheetOpen ? 'dialog' : mode === 'column' ? 'complementary' : undefined
      "
      :aria-modal="sheetOpen ? 'true' : undefined"
      :aria-label="row?.guestName || $t('reservations.detail.emptyTitle')"
    >
      <V2ReservationDetailHeader
        v-if="row"
        :reservation="row"
        :time-zone="zone"
        :late-minutes="late"
        @close="close"
      />
      <div v-else-if="loading" class="detail-loading">
        <PosUiSkeleton
          v-for="n in 5"
          :key="n"
          class="detail-skeleton"
        /><PosUiButton
          color="neutral"
          variant="ghost"
          icon="close"
          :aria-label="$t('close')"
          @click="close"
        />
      </div>
      <PosUiEmptyState
        v-else
        :icon="failed ? 'cloud-alert' : 'calendar-check-outline'"
        :title="
          $t(
            failed
              ? 'reservations.page.loadError'
              : 'reservations.detail.emptyTitle'
          )
        "
        :description="
          failed ? undefined : $t('reservations.detail.emptyDescription')
        "
      >
        <template v-if="failed" #action
          ><PosUiButton color="neutral" variant="outline" @click="load">{{
            $t('reservations.page.retry')
          }}</PosUiButton
          ><PosUiButton color="neutral" variant="ghost" @click="close">{{
            $t('close')
          }}</PosUiButton></template
        >
      </PosUiEmptyState>
      <V2ReservationStatusActions
        v-if="row"
        class="detail-actions"
        :reservation="row"
        :now="now"
        :time-zone="zone"
        :business-day-start-hour="cutover"
        :busy="busy"
        :can-manage="canManage"
        :can-orders="canOrders"
        @action="runAction"
      />
      <div v-if="row" class="detail-body">
        <div v-if="failed" class="load-error">
          <span>{{ $t('reservations.page.loadError') }}</span
          ><PosUiButton color="neutral" variant="ghost" @click="load">{{
            $t('reservations.page.retry')
          }}</PosUiButton>
        </div>
        <section class="detail-section">
          <div class="section-heading">
            <h3>{{ $t('reservations.detail.tables') }}</h3>
            <PosUiButton
              v-if="editable"
              color="neutral"
              variant="ghost"
              :disabled="busy"
              @click="setTables"
              >{{ $t('reservations.detail.changeTable') }}</PosUiButton
            >
          </div>
          <div class="table-chips">
            <PosUiBadge
              v-for="table in row.tables"
              :key="table.id"
              color="neutral"
              variant="soft"
              >{{
                $t('reservations.common.table', { name: table.name })
              }}</PosUiBadge
            ><span v-if="!row.tableIds.length" class="muted">{{
              $t('reservations.common.noTable')
            }}</span>
          </div>
          <p
            v-if="row.tablesAutoAssigned && row.tableIds.length"
            class="section-hint"
          >
            {{ $t('reservations.detail.autoAssigned') }}
          </p>
        </section>
        <section class="detail-section">
          <div class="section-heading">
            <h3>{{ $t('reservations.detail.schedule') }}</h3>
            <PosUiButton
              v-if="editable"
              color="neutral"
              variant="ghost"
              :disabled="busy"
              @click="edit"
              >{{ $t('reservations.detail.reschedule') }}</PosUiButton
            >
          </div>
          <p class="schedule-date">{{ formattedDate(row.startsAt) }}</p>
          <p class="schedule-time">
            {{ formattedTime(row.startsAt) }} – {{ formattedTime(row.endsAt)
            }}<span>
              ·
              {{
                $t('reservations.common.minutes', { minutes: row.durationMin })
              }}</span
            >
          </p>
          <p v-if="row.guestConfirmedAt" class="confirmed-hint">
            <PosUiIcon name="check-circle-outline" />{{
              $t('reservations.detail.guestConfirmed', {
                when: formattedTime(row.guestConfirmedAt),
              })
            }}
          </p>
        </section>
        <V2ReservationGuestCard :reservation="row" @guest="guest" />
        <V2ReservationDepositCard
          :reservation="row"
          :busy="busy"
          :can-orders="canOrders"
          @apply="applyDeposit"
          @refund="refundPaidDeposit"
        />
        <V2ReservationNotesEditor
          :reservation="row"
          :notes="draftNotes"
          :tags="draftTags"
          :dirty="dirty"
          :error="draftError"
          :busy="busy"
          :disabled="!canManage"
          @notes="notesChanged"
          @tags="tagsChanged"
          @save="drafts.flush()"
        />
        <section class="detail-section history-section">
          <button
            type="button"
            class="history-toggle"
            :aria-expanded="historyOpen"
            @click="historyOpen = !historyOpen"
          >
            <span>{{
              $t(
                historyOpen
                  ? 'reservations.detail.hideHistory'
                  : 'reservations.detail.showHistory'
              )
            }}</span
            ><PosUiIcon :name="historyOpen ? 'chevron-up' : 'chevron-down'" />
          </button>
          <div v-show="historyOpen">
            <V2ReservationEventsList
              :events="events"
              :time-zone="zone"
              :loading="loading"
            />
          </div>
          <p v-if="row.createdByName" class="section-hint">
            {{
              $t('reservations.detail.createdBy', { name: row.createdByName })
            }}
          </p>
        </section>
      </div>
      <V2ReservationDetailFooter
        v-if="row"
        :reservation="row"
        :editable="editable"
        :can-manage="canManage"
        :busy="busy"
        @edit="edit"
        @resend="resend"
        @copyPhone="copyPhone"
      />
    </section>
  </aside>
</template>

<style scoped lang="scss">
  .reservation-detail {
    @apply tw-min-h-0;
    &.is-column {
      @apply tw-relative tw-h-full tw-flex-shrink-0 tw-border-l tw-border-neutral-200;
      width: 26rem;
    }
    &.is-sheet {
      @apply tw-fixed tw-inset-0 tw-flex tw-justify-end;
      z-index: 60;
      .detail-content {
        width: min(420px, 100%);
      }
    }
    .detail-backdrop {
      @apply tw-absolute tw-inset-0 tw-bg-black/30;
    }
    .detail-content {
      @apply tw-relative tw-flex tw-h-full tw-min-h-0 tw-w-full tw-flex-col tw-bg-white tw-outline-none;
    }
    .detail-loading {
      @apply tw-flex tw-flex-col tw-gap-4 tw-p-5;
    }
    .detail-skeleton {
      @apply tw-h-12;
    }
    .detail-body {
      @apply tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-overscroll-contain;
    }
    .detail-actions {
      @apply tw-flex-shrink-0;
    }
    .detail-section {
      @apply tw-border-b tw-border-neutral-200 tw-px-5 tw-py-4;
      h3 {
        @apply tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
      }
    }
    .section-heading {
      @apply tw-mb-2 tw-flex tw-min-h-[44px] tw-items-center tw-justify-between tw-gap-2;
    }
    .section-hint {
      @apply tw-mt-1 tw-text-xs tw-text-neutral-400;
    }
    .muted {
      @apply tw-text-sm tw-text-neutral-500;
    }
    .table-chips {
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }
    .schedule-date {
      @apply tw-text-sm tw-font-medium tw-text-neutral-800;
    }
    .schedule-time {
      @apply tw-mt-1 tw-font-semibold tw-tabular-nums tw-text-neutral-900;
      span {
        @apply tw-text-xs tw-font-normal tw-text-neutral-500;
      }
    }
    .confirmed-hint {
      @apply tw-mt-2 tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-emerald-700;
    }
    .history-toggle {
      @apply tw-mb-3 tw-flex tw-min-h-[44px] tw-w-full tw-items-center tw-justify-between tw-text-sm tw-font-medium tw-text-neutral-600;
    }
    .load-error {
      @apply tw-flex tw-items-center tw-justify-between tw-gap-2 tw-bg-red-50 tw-px-5 tw-py-2 tw-text-sm tw-text-red-700;
    }
    @media (max-width: 767px) {
      &.is-sheet .detail-content {
        @apply tw-w-full;
      }
      .detail-actions {
        @apply tw-order-last tw-border-b-0 tw-border-t tw-bg-white;
        padding-bottom: max(12px, env(safe-area-inset-bottom));
      }
    }
  }
</style>

<style lang="scss">
  // The app isolates page content below its toolbar (5) and drawer (6).
  // Lift the existing host only while the fixed detail sheet is open.
  .content-shell.reservation-detail-overlay-host {
    position: relative;
    z-index: 7;
  }
</style>
